
import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  
  const faqItems: FAQItem[] = [
    {
      question: "Do I need any technical background to join this training?",
      answer: "Not at all! This training is specifically designed for beginners with zero coding experience. If you can use a smartphone or computer to browse the web, you have all the skills needed to get started."
    },
    {
      question: "Will I get support after the training?",
      answer: "Absolutely! You'll get access to our private community where you can ask questions, share your progress, and get feedback from me and other participants."
    },
    {
      question: "How is this different from other AI trainings?",
      answer: "Most trainings focus on theory or basic ChatGPT prompts. This workshop is hands-on and practical—you'll watch me build real applications from scratch using AI tools, and learn exactly how to do the same yourself."
    },
    {
      question: "What types of projects can I build after this training?",
      answer: "You'll be able to create websites, landing pages, web applications, SaaS products, online stores, dashboards, and more—all without writing code."
    },
    {
      question: "Is there a money-back guarantee?",
      answer: "Yes! If you attend the full training and don't feel you've received value, just let us know within 7 days and we'll issue a full refund. No questions asked."
    }
  ];
  
  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };
  
  return (
    <section id="faq" className="py-20 bg-nocode-gray">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-montserrat font-bold text-3xl md:text-4xl text-nocode-blue mb-4"
            >
              Got Questions? We've Got the Answers
            </motion.h2>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-24 h-1 bg-nocode-gradient mx-auto mb-6"
            />
          </div>
          
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden shadow-md"
              >
                <button
                  className="w-full text-left px-6 py-4 focus:outline-none flex justify-between items-center"
                  onClick={() => toggleAccordion(index)}
                >
                  <span className="font-montserrat font-medium text-gray-800">
                    {item.question}
                  </span>
                  <svg
                    className={`w-5 h-5 text-nocode-blue transition-transform duration-300 ${
                      activeIndex === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>
                
                <div
                  className={`px-6 overflow-hidden transition-all duration-300 ${
                    activeIndex === index ? 'max-h-96 pb-4' : 'max-h-0'
                  }`}
                >
                  <p className="text-gray-600">
                    {item.answer}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 text-center"
          >
            <p className="text-gray-600">
              Still have questions? Reach out to us on{" "}
              <a href="https://wa.me/2348039260734" className="text-nocode-purple hover:underline" target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
